/**
 * Reading measurements off a roof report.
 *
 * Every measuring service — Roofr, EagleView, iRoofing, Hover — prints the same
 * numbers under slightly different names. Rather than target one vendor, this
 * scans the report's text for a label followed by a number and maps the label
 * onto a column of `job_measurements`.
 *
 * It is deliberately forgiving and deliberately not trusted: whatever it finds
 * is used to *prefill* the measurement form, and a person confirms it before
 * anything is saved. A missed field costs a rep one number typed by hand; a
 * wrong field would cost a mispriced roof, so ambiguity resolves to nothing.
 */

import { isHoverReport, parseHoverRoof } from "./hoverReport.js";

/** One piece of text and where it sat on the page, in PDF points. */
export interface ReportCell {
  x: number;
  width: number;
  text: string;
}

/** A row of the page. `y` counts upward, as PDF coordinates do. */
export interface ReportRow {
  page: number;
  y: number;
  cells: readonly ReportCell[];
}

/**
 * How the derived measurements are worked out (Jonathon, 2026-08-05).
 *
 * These are not printed on any report — they are how this company turns a
 * measured roof into what it has to buy.
 */
/**
 * Ridge vent is 80% of the ridge on a shingle roof (ADR 0239).
 *
 * Jonathon, 2026-09-30: "the calculation for ridge vent (labor and material)
 * should be 80% of the ridge on shingle applications". It was a third until
 * then (2026-08-05, "ridge vent is just the ridge divided by 3"). Only shingle
 * templates carry a ridge vent line — standing seam puts its ridge vent under
 * the ridge itself — so this is the shingle rule without a roof type in it.
 */
export const RIDGE_VENT_SHARE_OF_RIDGE = 0.8;
/** "on average one 15' downspout every 25' of eave." */
export const DOWNSPOUT_EVERY_FT = 25;
export const DOWNSPOUT_LENGTH_FT = 15;

/**
 * A combined hip-and-ridge figure, under every name a report gives it.
 *
 * Tested against the label and the few characters in front of it, so that a
 * value printed under one of these names is never read as the ridge or as the
 * hip on its own. See the note on `hip_ridge_lf` below.
 */
const COMBINED_HIP_RIDGE =
  /\b(?:hip|hips)\s*(?:&|and|\+|\/)\s*(?:ridge|ridges)\b|\b(?:ridge|ridges)\s*(?:&|and|\+|\/)\s*(?:hip|hips)\b|\bridge\s*cap\b/i;

/**
 * Label patterns per column, first match wins. Longest/most specific first.
 *
 * `not` is the other half of that: a label this company shares with a
 * different measurement matches on the page long before the specific one does,
 * and the first match wins. Where that happens the occurrence is skipped and
 * the search carries on through the report rather than taking it.
 */
const PATTERNS: readonly {
  key: string;
  labels: readonly string[];
  not?: RegExp;
}[] = [
  // Areas. "squares" and "sq ft" are handled separately below.
  {
    key: "squares",
    labels: [
      "total roof area",
      "total pitched area",
      "total area",
      "roof area",
      "total squares",
    ],
  },
  {
    key: "flat_squares",
    labels: [
      "total flat area",
      "flat roof area",
      "low slope area",
      "flat area",
    ],
  },
  {
    key: "two_story_squares",
    labels: ["two story area", "two-story area", "2 story area"],
  },

  // Lengths.
  { key: "ridge_vent_lf", labels: ["ridge vent", "ridge vents"] },
  /*
   * Hip and ridge, which a report is as likely to add together as to print
   * apart.
   *
   * GAF's QuickMeasure prints all three: "Hips 166 ft", "Ridges 208 ft" and
   * "Ridge Cap 374 ft" — the last being the first two added up, because hip
   * and ridge cap is one product bought by the foot. Read as the ridge, 374
   * feet is not a rounding error: it is 125 feet of ridge vent where the roof
   * wants 69, and a ridge line on the work order that is nearly twice the
   * ridge.
   *
   * So a combined figure never lands in either column. It is read here, into
   * `combined` rather than into the measurements, and the form says so — the
   * rep splits it, because the report is the only thing that knows how.
   */
  {
    key: "hip_ridge_lf",
    labels: [
      "hip & ridge cap length",
      "hip and ridge cap",
      "hip & ridge cap",
      "hip/ridge cap",
      "ridge cap length",
      "ridge cap",
      "hip and ridge",
      "hip & ridge",
      "hip/ridge",
      "ridge and hip",
      "ridge & hip",
      "ridge/hip",
    ],
  },
  {
    key: "ridge_lf",
    labels: [
      "total ridge length",
      "ridges length",
      "ridge length",
      "ridges",
      "ridge",
    ],
    not: COMBINED_HIP_RIDGE,
  },
  {
    key: "hip_lf",
    labels: ["total hip length", "hips length", "hip length", "hips", "hip"],
    not: COMBINED_HIP_RIDGE,
  },
  {
    key: "valley_lf",
    labels: [
      "total valley length",
      "valleys length",
      "valley length",
      "valleys",
      "valley",
    ],
  },
  {
    key: "eave_lf",
    labels: [
      "total eave length",
      "eaves length",
      "eave length",
      "eaves",
      "eave",
      "drip edge",
    ],
  },
  {
    key: "rake_lf",
    labels: [
      "total rake length",
      "rakes length",
      "rake length",
      "rakes",
      "rake",
    ],
  },
  { key: "gutter_lf", labels: ["gutter length", "gutters", "gutter"] },
  {
    // "Step" on its own is what a QuickMeasure summary calls it.
    key: "step_flashing_lf",
    labels: ["step flashing length", "step flashing", "step"],
  },
  {
    // And "Flash". Both are guarded against the other's label: "flashing"
    // matches inside "step flashing", which is a different number.
    key: "flashing_lf",
    labels: [
      "wall flashing length",
      "wall flashing",
      "flashing length",
      "flashing",
      "flash",
    ],
    not: /\bstep\s*flash/i,
  },
  {
    key: "transition_lf",
    labels: ["transitions length", "transitions", "transition"],
  },
  {
    key: "flat_perimeter_lf",
    labels: ["flat perimeter", "low slope perimeter"],
  },
  { key: "penetrations", labels: ["penetrations", "pipe boots"] },
];

/** Numbers may carry thousands separators and a trailing unit. */
const NUMBER = String.raw`(-?[\d,]+(?:\.\d+)?)`;

/** A square is 100 square feet. Reports quote either; the app stores squares. */
const SQUARE_FEET = 100;

const AREA_KEYS = new Set(["squares", "flat_squares", "two_story_squares"]);

/**
 * Keys that are not columns on `job_measurements`.
 *
 * A combined hip-and-ridge figure is a real number off a real report and it
 * belongs to no box on the form. It is handed back on its own so the form can
 * say what it found and ask for the two halves.
 */
const COMBINED_KEYS = new Set(["hip_ridge_lf"]);

function toNumber(raw: string): number | null {
  const value = Number(raw.replace(/,/g, ""));
  return Number.isFinite(value) ? value : null;
}

/**
 * Find `label` in the text and return the number that follows it, along with
 * whatever unit was printed. Labels are matched on a word boundary so "ridge"
 * does not match inside "ridge vent".
 */
function findValue(
  text: string,
  label: string,
  /** Context this label must not appear in — see `PATTERNS`. */
  not?: RegExp,
): { value: number; unit: string } | null {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    // label, optional "(ft)" style qualifier, separator, number, optional unit,
    // and — where the report writes lengths the way a tape measure reads them —
    // the inches that follow.
    String.raw`\b${escaped}\b[^\n\r:=]{0,20}?[:=\s]\s*${NUMBER}\s*` +
      String.raw`(squares?|sq\.?\s*ft\.?|sf|ft\.?|feet|lf|')?` +
      String.raw`\s*(?:(\d{1,2})\s*(?:in\b|inches\b|"))?`,
    "gi",
  );

  /*
   * Every occurrence, not the first one.
   *
   * "Ridges 208 ft" and "Ridge Cap 374 ft" are both on the same report, and
   * which comes first on the page is the report's business rather than ours.
   * A denied occurrence is stepped over and the search carries on, so the
   * right number is still found when the wrong one is printed above it.
   */
  for (const match of text.matchAll(pattern)) {
    if (not) {
      // The label, the dozen characters in front of it and the word after it:
      // enough to see the "Hip & " or "Step " leading into it and the "Cap"
      // trailing it, and not enough to reach the line above or below.
      const at = match.index ?? 0;
      const context = text.slice(Math.max(0, at - 12), at + label.length + 10);
      if (not.test(context)) continue;
    }
    const value = toNumber(match[1] ?? "");
    if (value === null) continue;
    return {
      value: value + inchesAsFeet(match[2], match[3]),
      unit: (match[2] ?? "").toLowerCase(),
    };
  }
  return null;
}

/**
 * The inches half of "307ft 10in".
 *
 * Roofr prints every length that way and the first reader took the feet and
 * dropped the rest, so an eave of 307ft 10in was stored as 307 and a rake of
 * 86ft 9in as 86. Always short, never long — and the eave and the rake are what
 * the starter, the drip edge and the gutter are ordered off, so the error only
 * ever runs one way.
 *
 * Guarded on the unit being feet: a roof does not have 4432 squares and 10
 * inches, and "28 facets" followed by a stray number is not a measurement at
 * all. Anything over eleven inches is not inches either, and is refused rather
 * than added — a number nobody can explain is worse than a number that is
 * merely coarse.
 */
function inchesAsFeet(
  unit: string | undefined,
  inches: string | undefined,
): number {
  if (!inches) return 0;
  const feet = /^(ft\.?|feet|lf|')$/i.test((unit ?? "").trim());
  if (!feet) return 0;
  const value = Number(inches);
  if (!Number.isFinite(value) || value < 0 || value > 11) return 0;
  return value / 12;
}

/** Pitch is printed as "6/12", "6:12" or "6 / 12". */
function findPitch(text: string): string | null {
  const match =
    /\b(?:predominant|average|primary|main)?\s*pitch\b[^\n\r:=]{0,20}?[:=\s]\s*(\d{1,2})\s*[/:]\s*12/i.exec(
      text,
    );
  if (match) return `${Number(match[1])}/12`;
  return null;
}

/** Waste is printed as "Waste: 15%" or "Waste Factor 15". */
function findWaste(text: string): number | null {
  const match =
    /\bwaste(?:\s*factor)?\b[^\n\r:=]{0,20}?[:=\s]\s*([\d.]+)\s*%?/i.exec(text);
  return match ? toNumber(match[1] ?? "") : null;
}

/**
 * The per-pitch square breakdown.
 *
 * Roofr prints it as a column table, and columns are what made the old reader
 * wrong rather than merely incomplete:
 *
 *     Pitch         2/12   9/12    12/12
 *     Area (sqft)   288    2,008   2,138
 *     Squares       2.9    20.1    21.4
 *
 * Scanning for "a pitch followed by a number" walks straight from `9/12` into
 * the `12` of `12/12` and records twenty-one squares of nine-pitch roof as
 * twelve — then finds nothing at all for `12/12`, because the row ends. Which
 * is exactly what Jonathon saw.
 *
 * So the header row is read for its pitches, the value row for its numbers, and
 * they are paired by position. A row whose count does not match the header is
 * refused rather than zipped short — a steep charge on the wrong band is a
 * mispriced roof, and no number is safer than a plausible one.
 *
 * The report is read pitch by pitch and handed back as the two bands the
 * company charges in — 8/12 to 12/12, and 13/12 and up. Anything shallower
 * carries no steep charge and is already counted in the total; the flat band is
 * `flat_squares`. A roof over 16/12 is not a roof anybody walks, and Roofr does
 * not print a column for one.
 */
/**
 * The pitches a header row lists.
 *
 * Roofr writes them out — "2/12  9/12  12/12" — and GAF's QuickMeasure prints
 * the rise alone: "Pitch  2  4  5  6  8  13  14  17+". Both are the same row
 * with the same columns under it, so both are read; the written form is tried
 * first, because a bare number is only a pitch when nothing else on the row is
 * a pitch. "17+" is a column like any other, and lands above the steep bands
 * this company charges in, where it is discarded.
 */
function pitchesOn(header: string): number[] {
  const written = [...header.matchAll(/\b(\d{1,2})\s*[/:]\s*12\b/g)].map((m) =>
    Number(m[1]),
  );
  if (written.length > 0) return written;
  return [...header.replace(/^\s*pitch\b/i, "").matchAll(/\b(\d{1,2})\+?/g)]
    .map((m) => Number(m[1]))
    .filter((rise) => rise > 0 && rise <= 30);
}

function findPitchBreakdown(text: string): Record<string, number> {
  const lines = text.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const header = lines[i] ?? "";
    if (!/^\s*pitch\b/i.test(header)) continue;

    const pitches = pitchesOn(header);
    if (pitches.length === 0) continue;

    // "Squares" is already in the unit the app stores, so it is preferred even
    // though the area row comes first on the page — Roofr rounds the squares
    // itself, and dividing its own square footage down disagrees with it in the
    // second decimal. Only the rows immediately under the header are looked at,
    // so the waste table further down cannot be mistaken for the values.
    const window = lines.slice(i + 1, i + 4);
    const candidates = [
      ...window.filter((row) => /^\s*squares\b/i.test(row)),
      ...window.filter((row) => /^\s*area\b/i.test(row)),
    ];

    for (const row of candidates) {
      const isSquares = /^\s*squares\b/i.test(row);

      const numbers = [...row.matchAll(new RegExp(NUMBER, "g"))]
        .map((m) => toNumber(m[1] ?? ""))
        .filter((n): n is number => n !== null);
      if (numbers.length !== pitches.length) continue;

      let mid = 0;
      let high = 0;
      pitches.forEach((rise, column) => {
        // 7/12 carries no steep charge, so a column for it is read and
        // discarded rather than banded with the 8s.
        if (rise < 8 || rise > 16) return;
        const value = isSquares
          ? numbers[column]!
          : Math.round((numbers[column]! / SQUARE_FEET) * 100) / 100;
        if (value <= 0) return;
        if (rise <= 12) mid += value;
        else high += value;
      });

      const found: Record<string, number> = {};
      // Rounded once, after adding up. Rounding each column and then summing
      // drifts by a hundredth per column against the report it came from.
      if (mid > 0) found.steep_mid_squares = Math.round(mid * 100) / 100;
      if (high > 0) found.steep_high_squares = Math.round(high * 100) / 100;
      if (Object.keys(found).length > 0) return found;
    }
  }

  return {};
}

/** A word centred over a column, and the columns it might belong to. */
const centre = (cell: ReportCell) => cell.x + cell.width / 2;

/** What the two reports call the column they want ordered. */
const MARKER = /^(recommended|suggested)$/i;

/**
 * Which waste percentage the report recommends.
 *
 * Roofr offers seven and marks one. The mark is a shaded rectangle — invisible
 * to any text extraction — plus the word "Recommended" centred over the column.
 * The text alone gives no clue which one it sits above, so reading it as text
 * takes the first column every time. That is how a 16% roof was being ordered
 * at 10%, which on this report is five squares of shingle.
 *
 * Matching the centre of the label to the centre of each column settles it
 * exactly: on the reference report both land on 346.6.
 *
 * GAF's QuickMeasure does the same thing in its own words — the column it
 * wants is boxed in red and the word over it is "Suggested" — so both words
 * are the marker. The geometry is identical, because the problem is: neither
 * the box nor the shading is text.
 */
export function recommendedWaste(rows: readonly ReportRow[]): number | null {
  for (const row of rows) {
    const label = row.cells.find((c) => MARKER.test(c.text));
    // The paragraph beginning "Recommended waste is based on…" is prose, not a
    // marker, so the row has to be the label and nothing else.
    if (!label || row.cells.length !== 1) continue;

    // Below it on the page: PDF y counts upward, so that is a smaller y.
    const candidates = rows
      .filter((r) => r.page === row.page && r.y < row.y)
      .sort((a, b) => b.y - a.y);

    for (const candidate of candidates.slice(0, 2)) {
      if (!/^waste/i.test(candidate.cells[0]?.text ?? "")) continue;

      const columns = candidate.cells
        .map((cell) => ({
          cell,
          match: /^(\d{1,3}(?:\.\d+)?)\s*%$/.exec(cell.text),
        }))
        .filter((c) => c.match)
        .map((c) => ({ value: Number(c.match![1]), at: centre(c.cell) }));
      if (columns.length === 0) continue;

      const target = centre(label);
      const nearest = columns.reduce((best, column) =>
        Math.abs(column.at - target) < Math.abs(best.at - target)
          ? column
          : best,
      );
      return nearest.value;
    }
  }
  return null;
}

/**
 * Does this report offer a choice of waste percentages at all?
 *
 * The question matters because of what happens when `recommendedWaste` comes
 * back empty. Falling through to the text reader takes the first column, and on
 * a Roofr report the first column is 10% — the under-ordering this whole change
 * exists to fix. That fallback is right for a report with one waste figure
 * printed in a sentence, and wrong for one with a table the geometry failed to
 * read: the marker is a shaded box, so anything that moves the label out of its
 * own row (an extraction that splits the word, a layout Roofr changes next
 * year) silently puts the app back where it started.
 *
 * So a table with no readable recommendation is answered with nothing, and the
 * rep picks. A blank somebody has to fill in is recoverable. A plausible wrong
 * number that costs five squares of shingle is not.
 */
export function hasWasteTable(rows: readonly ReportRow[]): boolean {
  return rows.some(
    (row) =>
      /^waste/i.test(row.cells[0]?.text ?? "") &&
      row.cells.filter((cell) => /^\d{1,3}(?:\.\d+)?\s*%$/.test(cell.text))
        .length > 1,
  );
}

export interface ParsedRoofReport {
  values: Record<string, number>;
  averagePitch: string | null;
  wastePct: number | null;
  /** Columns the report had a number for — what the form should highlight. */
  matched: string[];
  /** Columns worked out from the others rather than read off the report. */
  derived: string[];
  /**
   * Numbers the report printed that belong to no single box.
   *
   * Today that is `hip_ridge_lf` and nothing else: a hip-and-ridge figure the
   * report added up for us. It is here rather than in `values` because no
   * arithmetic can take it apart, and dropping it silently is how a 374-foot
   * ridge cap became a 374-foot ridge.
   */
  combined: Record<string, number>;
}

/**
 * What to say when the report added the hip and the ridge together.
 *
 * Only worth saying when one of the two halves is missing: a report that
 * prints "Hips 166", "Ridges 208" and "Ridge Cap 374" has already answered the
 * question, and repeating it back would be noise on every QuickMeasure read.
 * When the combined figure is all there is, nothing here can split it — the
 * two numbers are a fact about the roof that only the report holds — so it is
 * handed to the rep with the number it found.
 */
export function combinedHipRidgeNote(
  parsed: Pick<ParsedRoofReport, "values" | "combined">,
): string | null {
  const total = parsed.combined.hip_ridge_lf;
  if (total == null || total <= 0) return null;
  const ridge = parsed.values.ridge_lf;
  const hip = parsed.values.hip_lf;
  if (ridge != null && hip != null) return null;
  return (
    `This report gives hip and ridge as one number — ${total} LF — and that ` +
    `is not the ridge and it is not the hip. Enter them separately below: ` +
    `the ridge drives the ridge vent, and the hip does not.`
  );
}

/**
 * The measurements this company works out for itself.
 *
 * A roof report says how long the ridge is; it does not say how much ridge vent
 * to buy, and it has never heard of a downspout. Jonathon's rules, 2026-08-05:
 *
 *   - ridge vent is 80% of the ridge (2026-09-30; a third until then)
 *   - "gutter is eave times waste percentage"
 *   - "downspout which on average is one 15' downspout every 25' of eave"
 *
 * Gutter is the eave length *plus* its waste — the eave is how much gutter the
 * house needs, and the waste allowance covers the cuts. The shorthand above
 * reads as a multiplication, but eave times 0.16 would be a fifth of a house's
 * worth of gutter, which is not a quantity anybody orders. Confirmed with
 * Jonathon on 2026-08-08: 308 feet of eave at 16% is 357 feet of gutter.
 *
 * Nothing here overwrites a figure the report actually printed. A measured
 * number always beats a calculated one.
 */
export function deriveMeasurements(
  values: Record<string, number>,
  wastePct: number | null,
): Record<string, number> {
  const derived: Record<string, number> = {};
  const round = (n: number) => Math.round(n * 100) / 100;

  const ridge = values.ridge_lf;
  if (ridge != null && values.ridge_vent_lf == null) {
    derived.ridge_vent_lf = round(ridge * RIDGE_VENT_SHARE_OF_RIDGE);
  }

  const eave = values.eave_lf;
  if (eave != null && values.gutter_lf == null) {
    derived.gutter_lf = round(eave * (1 + (wastePct ?? 0) / 100));
  }

  // Counted off the eave itself, not the gutter: the spacing is a fact about
  // the house, and adding waste to it would buy a downspout nobody hangs.
  if (eave != null && values.downspout_ea == null) {
    const count = Math.ceil(eave / DOWNSPOUT_EVERY_FT);
    if (count > 0) {
      derived.downspout_ea = count;
      derived.downspout_lf = count * DOWNSPOUT_LENGTH_FT;
    }
  }

  return derived;
}

/**
 * Pull what we can out of a roof report's text.
 *
 * Areas are normalised to squares: a value printed in square feet is divided by
 * 100, a value already in squares is kept. When a report gives no unit, a
 * figure over 1000 is assumed to be square feet — no residential roof in this
 * business is a thousand squares, and none is under ten square feet.
 */
export function parseRoofReport(
  text: string,
  rows: readonly ReportRow[] = [],
): ParsedRoofReport {
  /*
   * A Hover report is read by its own labels. Its roof summary puts a facet
   * count before each length and its waste table has no recommendation, and
   * the label-then-number scan below read 2805 Saint Patrick Place as a
   * one-square roof with 1606% waste. See hoverReport.ts.
   */
  if (isHoverReport(text)) {
    const hover = parseHoverRoof(text);
    const values = { ...hover.values };
    const matched = Object.keys(values);
    const derived = deriveMeasurements(values, null);
    Object.assign(values, derived);
    return {
      values,
      averagePitch: hover.averagePitch,
      wastePct: null,
      matched,
      derived: Object.keys(derived),
      combined: {},
    };
  }

  const values: Record<string, number> = {};
  const combined: Record<string, number> = {};

  for (const { key, labels, not } of PATTERNS) {
    for (const label of labels) {
      const hit = findValue(text, label, not);
      if (!hit) continue;

      let value = hit.value;
      if (AREA_KEYS.has(key)) {
        // "squares" also starts with "sq", so it has to be ruled out first.
        const inSquares = hit.unit.startsWith("square");
        const inFeet = !inSquares && /^(sq|sf|ft|feet)/.test(hit.unit);
        if (inFeet || (!inSquares && value > 1000)) value /= SQUARE_FEET;
      }
      // Two decimals for everything, not just areas: a length carrying inches
      // is a third of a foot more often than not, and 307.83 is a number a rep
      // can check against the report where 307.8333333333333 is not.
      value = Math.round(value * 100) / 100;
      if (value <= 0) continue;

      if (COMBINED_KEYS.has(key)) combined[key] = value;
      else values[key] = value;
      break;
    }
  }

  Object.assign(values, findPitchBreakdown(text));

  // The marked column beats the first one the text happens to mention — and
  // where the report offers a table but the mark could not be read, nothing
  // beats the first column, which is the wrong one by construction.
  const marked = recommendedWaste(rows);
  const wastePct = marked ?? (hasWasteTable(rows) ? null : findWaste(text));

  const matched = Object.keys(values);
  const derived = deriveMeasurements(values, wastePct);
  Object.assign(values, derived);

  return {
    values,
    averagePitch: findPitch(text),
    wastePct,
    matched,
    derived: Object.keys(derived),
    combined,
  };
}
